import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkforceBench } from "@/hooks/workforce/useWorkforceBench";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	Users,
	Clock,
	AlertTriangle,
	Search,
	ExternalLink,
	TrendingDown,
	Calendar,
	Building2,
	Briefcase,
	Plus,
	Tag,
	Pencil,
	Square,
	RotateCcw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { AddToBenchModal } from "./AddToBenchModal";
import { StopBenchModal } from "./StopBenchModal";
import { BenchEditModal } from "./BenchEditModal";
import { FindJobsModal } from "./FindJobsModal";
import { toast } from "@/hooks/use-toast";

type BenchFilter = "active" | "past";

interface WorkforceBenchProps {
	onFindAssignment?: (candidateId: string) => void;
}

export const WorkforceBench = ({ onFindAssignment }: WorkforceBenchProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { benchCandidates, stats, isLoading, refetch, stopBench, reopenBench } = useWorkforceBench();
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [stopBenchTarget, setStopBenchTarget] = useState<{ workforceId: string; name: string } | null>(null);
	const [stoppingId, setStoppingId] = useState<string | null>(null);
	const [reopeningId, setReopeningId] = useState<string | null>(null);
	const [editWorkforceId, setEditWorkforceId] = useState<string | null>(null);
	const [benchFilter, setBenchFilter] = useState<BenchFilter>("active");
	const [findJobsTarget, setFindJobsTarget] = useState<{ id: string; name: string } | null>(null);

	// Filter bench candidates by status and search term
	const filteredCandidates = useMemo(() => {
		let filtered = benchCandidates;

		// Filter by status
		if (benchFilter === "active")
			filtered = filtered.filter(c => c.status === 'active' || c.status === 'on_hold');
		else
			filtered = filtered.filter(c => c.status === 'completed' || c.status === 'terminated');

		// Filter by search term
		if (searchTerm.trim()) {
			const term = searchTerm.toLowerCase().trim();
			filtered = filtered.filter(c =>
				c.name.toLowerCase().includes(term) ||
				c.function?.toLowerCase().includes(term) ||
				c.lastAssignmentCompany?.toLowerCase().includes(term) ||
				c.lastAssignmentRole?.toLowerCase().includes(term)
			);
		}

		return filtered;
	}, [benchCandidates, searchTerm, benchFilter]);

	const activeBenchCount = useMemo(() =>
		benchCandidates.filter(c => c.status === 'active' || c.status === 'on_hold').length
	, [benchCandidates]);

	const pastBenchCount = useMemo(() =>
		benchCandidates.filter(c => c.status === 'completed' || c.status === 'terminated').length
	, [benchCandidates]);

	const handleStopBench = async (data: { reason: string; note?: string }) => {
		if (!stopBenchTarget) return;
		setStoppingId(stopBenchTarget.workforceId);
		const success = await stopBench(stopBenchTarget.workforceId, data);
		setStoppingId(null);
		setStopBenchTarget(null);

		if (success) {
			toast({
				title: t("workforce.bench.stopSuccess", "Bench period ended"),
				description: t("workforce.bench.stopSuccessDesc", "{{name}}'s bench period has been ended", { name: stopBenchTarget.name }),
			});
		} else {
			toast({
				title: t("common.error", "Error"),
				description: t("workforce.bench.stopFailed", "Failed to end bench period"),
				variant: "destructive",
			});
		}
	};

	const handleReopenBench = async (workforceId: string, name: string) => {
		setReopeningId(workforceId);
		const success = await reopenBench(workforceId);
		setReopeningId(null);

		if (success) {
			toast({
				title: t("workforce.bench.reopenSuccess", "Bench period re-opened"),
				description: t("workforce.bench.reopenSuccessDesc", "{{name}}'s bench period has been re-opened", { name }),
			});
		} else {
			toast({
				title: t("common.error", "Error"),
				description: t("workforce.bench.reopenFailed", "Failed to re-open bench period"),
				variant: "destructive",
			});
		}
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					{[1, 2, 3, 4].map(i => (
						<Skeleton key={i} className="h-24 rounded-sm" />
					))}
				</div>
				<Skeleton className="h-96 rounded-sm" />
			</div>
		);
	}

	const getBenchSeverity = (days: number): 'low' | 'medium' | 'high' | 'critical' => {
		if (days <= 7) return 'low';
		if (days <= 30) return 'medium';
		if (days <= 60) return 'high';
		return 'critical';
	};

	const getSeverityColor = (severity: string) => {
		switch (severity) {
			case 'low':
				return 'bg-muted text-muted-foreground';
			case 'medium':
				return 'bg-muted text-foreground';
			case 'high':
				return 'bg-muted text-foreground font-medium';
			case 'critical':
				return 'bg-muted text-foreground font-semibold';
			default:
				return '';
		}
	};

	const isActive = benchFilter === "active";

	return (
		<div className="space-y-4">
			{/* Bench Stats - compact */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
				<Card className="p-3">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-xs text-muted-foreground">{t("workforce.bench.onBench", "On Bench")}</p>
							<p className="text-xl font-bold">{stats.totalOnBench}</p>
						</div>
						<Users className="h-5 w-5 text-muted-foreground" />
					</div>
				</Card>
				<Card className="p-3">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-xs text-muted-foreground">{t("workforce.bench.dailyCost", "Daily Bench Cost")}</p>
							<p className="text-xl font-bold">{stats.currency} {stats.totalDailyBenchCost.toLocaleString()}</p>
						</div>
						<TrendingDown className="h-5 w-5 text-muted-foreground" />
					</div>
				</Card>
				<Card className="p-3">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-xs text-muted-foreground">{t("workforce.bench.avgDays", "Average Days")}</p>
							<p className="text-xl font-bold">{stats.averageDaysOnBench}</p>
						</div>
						<Clock className="h-5 w-5 text-muted-foreground" />
					</div>
				</Card>
				<Card className="p-3">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-xs text-muted-foreground">{t("workforce.bench.longest", "Longest on Bench")}</p>
							<p className="text-xl font-bold">{stats.longestOnBench} {t("workforce.bench.days", "days")}</p>
						</div>
						<AlertTriangle className="h-5 w-5 text-muted-foreground" />
					</div>
				</Card>
			</div>

			{/* Filter + Search + Add Button */}
			<div className="flex items-center gap-3">
				{/* Active / Past toggle */}
				<div className="flex rounded-sm border overflow-hidden">
					<button
						className={cn(
							"px-3 py-1.5 text-sm font-medium transition-colors",
							benchFilter === "active"
								? "bg-primary text-primary-foreground"
								: "bg-background text-muted-foreground hover:text-foreground"
						)}
						onClick={() => setBenchFilter("active")}
					>
						{t("workforce.bench.filterActive", "Active")} ({activeBenchCount})
					</button>
					<button
						className={cn(
							"px-3 py-1.5 text-sm font-medium transition-colors border-l",
							benchFilter === "past"
								? "bg-primary text-primary-foreground"
								: "bg-background text-muted-foreground hover:text-foreground"
						)}
						onClick={() => setBenchFilter("past")}
					>
						{t("workforce.bench.filterPast", "Past")} ({pastBenchCount})
					</button>
				</div>

				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("workforce.bench.searchPlaceholder", "Search consultants...")}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Button onClick={() => setIsAddModalOpen(true)} size="sm">
					<Plus className="h-4 w-4 mr-2" />
					{t("workforce.bench.addToBench", "Add to Bench")}
				</Button>
			</div>

			{/* Bench Candidates Table */}
			{filteredCandidates.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center">
						{benchFilter === "active" && activeBenchCount === 0 && !searchTerm.trim() ? (
							<>
								<Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold">
									{t("workforce.bench.noBench", "No one on the bench!")}
								</h3>
								<p className="text-muted-foreground mt-1">
									{t("workforce.bench.noBenchDesc", "All your consultants are currently on active assignments.")}
								</p>
								<Button
									variant="outline"
									className="mt-4"
									onClick={() => setIsAddModalOpen(true)}
								>
									<Plus className="h-4 w-4 mr-2" />
									{t("workforce.bench.addManually", "Add consultant manually")}
								</Button>
							</>
						) : benchFilter === "past" && pastBenchCount === 0 && !searchTerm.trim() ? (
							<>
								<Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold">
									{t("workforce.bench.noPast", "No past bench entries")}
								</h3>
								<p className="text-muted-foreground mt-1">
									{t("workforce.bench.noPastDesc", "Completed bench periods will appear here.")}
								</p>
							</>
						) : (
							<>
								<Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold">
									{t("workforce.bench.noResults", "No results found")}
								</h3>
								<p className="text-muted-foreground mt-1">
									{t("workforce.bench.noResultsDesc", "Try adjusting your search term.")}
								</p>
							</>
						)}
					</CardContent>
				</Card>
			) : (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<Users className="h-4 w-4 text-muted-foreground" />
							{isActive
								? t("workforce.bench.title", "Bench Management")
								: t("workforce.bench.pastTitle", "Past Bench Entries")}
							<Badge variant="secondary" className="ml-2">
								{filteredCandidates.length} {t("workforce.bench.consultants", "consultants")}
							</Badge>
						</CardTitle>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("workforce.bench.consultant", "Consultant")}</TableHead>
									<TableHead>{t("workforce.bench.lastAssignment", "Last Assignment")}</TableHead>
									<TableHead className="text-center">{t("workforce.bench.daysOnBench", "Days on Bench")}</TableHead>
									<TableHead className="text-right">{t("workforce.bench.dailyCost", "Daily Bench Cost")}</TableHead>
									<TableHead className="text-right">{t("workforce.bench.effectiveCost", "Effective Cost")}</TableHead>
									<TableHead className="text-right">{t("common.actions", "Actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredCandidates.map((candidate) => {
									const severity = getBenchSeverity(candidate.daysOnBench);
									return (
										<TableRow key={candidate.workforceId} className="group">
											<TableCell>
												<div className="flex items-center gap-3">
													<Avatar className="h-10 w-10">
														<AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
															{candidate.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
														</AvatarFallback>
													</Avatar>
													<div>
														<div className="flex items-center gap-2">
															<button
																className="font-medium hover:text-primary hover:underline transition-colors text-left"
																onClick={() => navigate(`/candidates/${candidate.id}`)}
															>
																{candidate.name}
															</button>
															{candidate.benchReason && (
																<TooltipProvider>
																	<Tooltip>
																		<TooltipTrigger>
																			<Badge variant="outline" className="text-xs px-1.5 py-0">
																				<Tag className="h-3 w-3 mr-1" />
																				{t(`workforce.bench.reasons.${candidate.benchReason}`, candidate.benchReason)}
																			</Badge>
																		</TooltipTrigger>
																		<TooltipContent>
																			{candidate.notes && (
																				<p className="text-xs">{candidate.notes}</p>
																			)}
																		</TooltipContent>
																	</Tooltip>
																</TooltipProvider>
															)}
															{!isActive && (
																<Badge variant="outline" className="text-xs">
																	{candidate.status === 'completed'
																		? t("workforce.statuses.completed", "Completed")
																		: t("workforce.statuses.terminated", "Terminated")}
																</Badge>
															)}
														</div>
														{candidate.function && (
															<div className="text-sm text-muted-foreground">{candidate.function}</div>
														)}
													</div>
												</div>
											</TableCell>
											<TableCell>
												<div className="space-y-1">
													{candidate.lastAssignmentRole && (
														<div className="flex items-center gap-1 text-sm">
															<Briefcase className="h-3 w-3 text-muted-foreground" />
															{candidate.lastAssignmentRole}
														</div>
													)}
													{candidate.lastAssignmentCompany && (
														<div className="flex items-center gap-1 text-sm text-muted-foreground">
															<Building2 className="h-3 w-3" />
															{candidate.lastAssignmentCompany}
														</div>
													)}
													<div className="flex items-center gap-1 text-xs text-muted-foreground">
														<Calendar className="h-3 w-3" />
														{t("workforce.bench.started", "Started")} {format(new Date(candidate.benchStartDate), 'MMM d, yyyy')}
														{candidate.endDate && (
															<span> — {t("workforce.bench.ended", "Ended")} {format(new Date(candidate.endDate), 'MMM d, yyyy')}</span>
														)}
													</div>
												</div>
											</TableCell>
											<TableCell className="text-center">
												<Badge className={cn("font-mono", getSeverityColor(severity))}>
													{candidate.daysOnBench} {t("workforce.bench.days", "days")}
												</Badge>
											</TableCell>
											<TableCell className="text-right">
												{candidate.dailyCost != null ? (
													<span className="font-medium">
														{candidate.currency} {candidate.dailyCost.toLocaleString()}
													</span>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
											<TableCell className="text-right">
												{candidate.benchCost != null ? (
													<span className="font-medium">
														{candidate.currency} {candidate.benchCost.toLocaleString()}
													</span>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
											<TableCell className="text-right">
												<div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
													{isActive ? (
														<>
															<TooltipProvider>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			variant="ghost"
																			size="sm"
																			onClick={() => setStopBenchTarget({ workforceId: candidate.workforceId, name: candidate.name })}
																			disabled={stoppingId === candidate.workforceId}
																		>
																			<Square className="h-4 w-4" />
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		{t("workforce.bench.stopBench", "End bench")}
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
															<TooltipProvider>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			variant="ghost"
																			size="sm"
																			onClick={() => setEditWorkforceId(candidate.workforceId)}
																		>
																			<Pencil className="h-4 w-4" />
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		{t("common.edit", "Edit")}
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
															<TooltipProvider>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			variant="ghost"
																			size="sm"
																			onClick={() => navigate(`/workforce/${candidate.workforceId}`)}
																		>
																			<ExternalLink className="h-4 w-4" />
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		{t("workforce.bench.openAssignment", "Open assignment")}
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
															<Button
																variant="default"
																size="sm"
																onClick={() => setFindJobsTarget({ id: candidate.id, name: candidate.name })}
															>
																<Search className="h-4 w-4 mr-1" />
																{t("workforce.bench.findJob", "Find Job")}
															</Button>
														</>
													) : (
														<>
															<TooltipProvider>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			variant="ghost"
																			size="sm"
																			onClick={() => handleReopenBench(candidate.workforceId, candidate.name)}
																			disabled={reopeningId === candidate.workforceId}
																		>
																			<RotateCcw className="h-4 w-4" />
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		{t("workforce.bench.reopen", "Re-open bench")}
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
															<TooltipProvider>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Button
																			variant="ghost"
																			size="sm"
																			onClick={() => navigate(`/workforce/${candidate.workforceId}`)}
																		>
																			<ExternalLink className="h-4 w-4" />
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent>
																		{t("workforce.bench.openAssignment", "Open assignment")}
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
														</>
													)}
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>

						{/* Total Cost Summary - only for active */}
						{isActive && (
							<div className="mt-6 pt-4 border-t flex items-center justify-between">
								<div className="text-muted-foreground">
									{t("workforce.bench.totalBenchCost", "Total accumulated bench cost")}
								</div>
								<div className="text-2xl font-bold">
									{stats.currency} {filteredCandidates.reduce((sum, c) => sum + (c.benchCost || 0), 0).toLocaleString()}
								</div>
							</div>
						)}
					</CardContent>
				</Card>
			)}

			{/* Add to Bench Modal */}
			<AddToBenchModal
				open={isAddModalOpen}
				onOpenChange={setIsAddModalOpen}
				onSuccess={refetch}
			/>

			{/* Stop Bench Modal */}
			<StopBenchModal
				open={!!stopBenchTarget}
				onOpenChange={(open) => { if (!open) setStopBenchTarget(null); }}
				candidateName={stopBenchTarget?.name || ""}
				onConfirm={handleStopBench}
				isLoading={!!stoppingId}
			/>

			{/* Edit Bench Modal */}
			<BenchEditModal
				workforceId={editWorkforceId}
				onOpenChange={(open) => { if (!open) setEditWorkforceId(null); }}
				onSuccess={refetch}
			/>

			{/* Find Jobs Modal */}
			{findJobsTarget && (
				<FindJobsModal
					candidateId={findJobsTarget.id}
					candidateName={findJobsTarget.name}
					open={!!findJobsTarget}
					onOpenChange={(open) => { if (!open) setFindJobsTarget(null); }}
				/>
			)}
		</div>
	);
};
